/**
 * Typed API client.
 *
 * Everything goes through the generated OpenAPI types, so a renamed field is a
 * compile error rather than a runtime `undefined`. Components never call this
 * directly -- they use the hooks in each feature's api.ts.
 */
import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./generated/schema";
import {
  getAccessToken,
  onSessionRefreshed,
  onUnauthorized,
  setAccessToken,
} from "@/lib/auth/token-store";

/**
 * Origin only -- NOT including /api/v1.
 *
 * The generated path types already carry the full "/api/v1/..." path, so a
 * base URL containing the prefix would produce /api/v1/api/v1/... and fail
 * type-checking against `paths`.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:8000";

/** The error envelope every non-2xx response uses (api-contract skill). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown> | null;
    field_errors?: Array<{
      field: string;
      code: string;
      message: string;
    }> | null;
    request_id?: string | null;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | null;
  readonly fieldErrors: Array<{ field: string; code: string; message: string }>;
  readonly requestId: string | null;

  constructor(status: number, body: ApiErrorBody | undefined) {
    super(body?.error?.message ?? "Something went wrong.");
    this.name = "ApiError";
    this.status = status;
    // Branch on `code`, never on `message`: messages are translated.
    this.code = body?.error?.code ?? "UNKNOWN_ERROR";
    this.details = body?.error?.details ?? null;
    this.fieldErrors = body?.error?.field_errors ?? [];
    this.requestId = body?.error?.request_id ?? null;
  }
}

/** Endpoints that must never trigger a refresh -- refreshing them recurses. */
const AUTH_PATHS = [
  "/api/v1/auth/refresh",
  "/api/v1/auth/login",
  "/api/v1/auth/logout",
];

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.some((path) => url.includes(path));
}

/**
 * The one in-flight refresh.
 *
 * Refresh tokens rotate, and presenting a rotated one is how the server
 * detects theft -- it revokes the whole chain. A page load fires several
 * queries at once, so they expire together and all get a 401 together; letting
 * each start its own refresh would send the same token three times and get the
 * user forcibly signed out. Every caller awaits the same promise instead.
 */
let refreshInFlight: Promise<RefreshedSession | null> | null = null;

/**
 * What /auth/refresh hands back. Deliberately loose -- the transport layer has
 * no business importing the app's session type, and the caller that cares
 * casts it.
 */
export interface RefreshedSession {
  access_token: string;
  expires_in: number;
}

/**
 * Returns the renewed session, or null if it could not be renewed.
 *
 * Returns the payload rather than a bare boolean so a caller can apply it
 * directly instead of waiting to be called back. That matters for the one on
 * page load: the callback is registered by an effect that StrictMode unmounts
 * and remounts, and a response arriving inside that window would be delivered
 * to nobody and leave the app stuck on its loading screen.
 */
export function refreshSession(): Promise<RefreshedSession | null> {
  refreshInFlight ??= (async () => {
    try {
      // Plain fetch, not `api.POST`: going back through this middleware on a
      // 401 from the refresh endpoint itself would recurse.
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return null;

      const session = (await response.json()) as RefreshedSession;
      setAccessToken(session.access_token, session.expires_in);
      onSessionRefreshed(session);
      return session;
    } catch {
      // Offline, or the server is down. Not an auth failure -- do not sign the
      // user out over a dropped connection.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Requests kept for replay.
 *
 * A Request's body is consumed once it is sent, so retrying after a refresh
 * needs a clone taken before the fetch.
 */
const replayable = new Map<string, Request>();

const authMiddleware: Middleware = {
  onRequest({ request, id }) {
    const token = getAccessToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    if (!request.headers.has("X-Request-Id")) {
      request.headers.set("X-Request-Id", crypto.randomUUID());
    }
    // Every POST gets a key unless the caller minted its own. The key is
    // attached to the Request, so the clone below -- the one replayed after a
    // silent token refresh -- carries the same one, and the server replays its
    // first answer instead of taking the money a second time.
    //
    // A caller that needs the key to survive longer than one Request (a submit
    // the user can tap twice) passes its own; see the collection screen.
    if (request.method === "POST" && !request.headers.has("Idempotency-Key")) {
      request.headers.set("Idempotency-Key", crypto.randomUUID());
    }
    if (!isAuthPath(request.url)) replayable.set(id, request.clone());
    return request;
  },

  async onResponse({ response, id }) {
    const original = replayable.get(id);
    replayable.delete(id);

    if (response.status !== 401 || !original) return response;

    // The access token has expired (or was rejected). Renew once, then replay
    // the request exactly as it was sent.
    const renewed = await refreshSession();
    if (!renewed) {
      onUnauthorized();
      return response;
    }

    const retry = original.clone();
    retry.headers.set("Authorization", `Bearer ${getAccessToken() ?? ""}`);
    const replayed = await fetch(retry);
    // Still 401 after a successful refresh means the session is genuinely
    // over -- a revoked user, or a mahallu membership that was removed.
    if (replayed.status === 401) onUnauthorized();
    return replayed;
  },

  onError({ id }) {
    replayable.delete(id);
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  // The refresh token is an HttpOnly cookie on another origin.
  credentials: "include",
});

api.use(authMiddleware);

/** Unwrap an openapi-fetch result, throwing a typed ApiError on failure. */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error !== undefined) {
    throw new ApiError(
      result.response.status,
      result.error as ApiErrorBody | undefined,
    );
  }
  if (result.data === undefined) {
    // 204 No Content is a success with nothing to return, and every DELETE in
    // this API answers that way. Treating an absent body as failure made a
    // successful delete throw after the server had already done the work:
    // onSuccess never ran, the cache was never invalidated, and the row the
    // user had just deleted stayed on screen under an error banner.
    if (result.response.ok) return undefined as T;
    throw new ApiError(result.response.status, undefined);
  }
  return result.data;
}
